import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwm211Api, FM030, FM033 } from '../../../services/pwm211Api';

interface ReplyState {
  open: boolean;
  record: FM030 | null;
  replies: FM033[];
  loading: boolean;
  saving: boolean;
}

function emptyReply(loc: string, product: string, batch: string, seq: number): FM033 {
  return {
    fm033_01: loc,
    fm033_02: product,
    fm033_03: batch,
    fm033_04: seq,
    fm033_11: '',
    fm033_12: '',
    fm033_13: '',
    fm033_14: null,
    fm033_15: '',
    fm033_21: null,
  };
}

export function usePWM211Reply() {
  const [state, setState] = useState<ReplyState>({
    open: false,
    record: null,
    replies: [],
    loading: false,
    saving: false,
  });

  // Open the reply modal and load FM030 with FM033 replies
  const openReply = useCallback(async (row: FM030) => {
    setState(prev => ({ ...prev, open: true, loading: true, record: row }));
    try {
      const full = await pwm211Api.get(row.fm030_01, row.fm030_02, row.fm030_03);
      setState(prev => ({
        ...prev,
        record: full,
        replies: full.replies ?? [],
        loading: false,
      }));
    } catch {
      message.error('載入回覆明細失敗');
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const closeReply = useCallback(() => {
    setState(prev => ({ ...prev, open: false, record: null, replies: [] }));
  }, []);

  // Add a new empty reply row
  const addReply = useCallback(() => {
    setState(prev => {
      if (!prev.record) return prev;
      const nextSeq = prev.replies.length + 1;
      const newRow = emptyReply(
        prev.record.fm030_01,
        prev.record.fm030_02,
        prev.record.fm030_03,
        nextSeq
      );
      return { ...prev, replies: [...prev.replies, newRow] };
    });
  }, []);

  // Remove a reply row by index
  const removeReply = useCallback((idx: number) => {
    setState(prev => {
      const updated = prev.replies
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, fm033_04: i + 1 }));
      return { ...prev, replies: updated };
    });
  }, []);

  // Update a field in a reply row
  const setReplyField = useCallback((idx: number, field: keyof FM033, value: unknown) => {
    setState(prev => {
      const updated = prev.replies.map((r, i) =>
        i === idx ? { ...r, [field]: value } : r
      );
      return { ...prev, replies: updated };
    });
  }, []);

  // Save (upsert) replies
  const saveReplies = useCallback(async (): Promise<boolean> => {
    const { record, replies } = state;
    if (!record) return false;
    setState(prev => ({ ...prev, saving: true }));
    try {
      await pwm211Api.update(
        record.fm030_01,
        record.fm030_02,
        record.fm030_03,
        { master: record, details: replies }
      );
      message.success('儲存成功');
      setState(prev => ({ ...prev, saving: false }));
      return true;
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { errors?: { message: string }[] } } };
        const errors = axiosErr.response?.data?.errors;
        if (errors && Array.isArray(errors)) {
          message.error(errors.map((e: { message: string }) => e.message).join('; '));
        } else {
          message.error('儲存失敗，請稍後再試');
        }
      } else {
        message.error('儲存失敗，請稍後再試');
      }
      setState(prev => ({ ...prev, saving: false }));
      return false;
    }
  }, [state]);

  return {
    replyOpen: state.open,
    replyRecord: state.record,
    replies: state.replies,
    replyLoading: state.loading,
    replySaving: state.saving,
    openReply,
    closeReply,
    addReply,
    removeReply,
    setReplyField,
    saveReplies,
  };
}
